//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { SamplingCampaignRequestCountry } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { CampaignRequest } from '@apis/campaign-requests/shared/CampaignRequests';
import { CAMPAIGN_REQUEST_FORM_VALIDATION } from '@campaigns/constants/CampaignRequestFormValidationConstants';
import { CAMPAIGN_REQUEST_LOCALES } from '@campaigns/constants/CampaignRequestsLocaleConstants';
import { CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/CampaignsTrackingId';
import { BodyText } from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import DropdownSelect, {
  SelectItemValue,
} from '@components/molecules/dropdown-select/DropdownSelect';
import { isDefined } from '@utils/filterPredicates';

interface CampaignRequestLocaleFieldProps {
  availableCountryCodes?: SamplingCampaignRequestCountry[];
}

export const CampaignRequestLocaleField = ({
  availableCountryCodes,
}: CampaignRequestLocaleFieldProps) => {
  const { control, setValue } = useFormContext<CampaignRequest>();

  const getFilteredLocales = () => {
    return CAMPAIGN_REQUEST_LOCALES.filter((locale) => {
      const isCountryMatch =
        !isDefined(availableCountryCodes) ||
        availableCountryCodes.length === 0 ||
        availableCountryCodes.includes(locale.country);

      return isCountryMatch;
    });
  };

  const filteredLocales = getFilteredLocales();

  return (
    <div>
      <BodyText>Locale</BodyText>
      <FormFieldController
        control={control}
        name='locale'
        rules={CAMPAIGN_REQUEST_FORM_VALIDATION.locale}
      >
        {(props) => {
          const handleSelect = (localeValue: SelectItemValue) => {
            // Update the locale value
            props.onChange(localeValue);
            // Find the selected locale object
            const selectedLocale = CAMPAIGN_REQUEST_LOCALES.find(
              (locale) => locale.value === localeValue,
            );
            if (isDefined(selectedLocale)) {
              // Update the audienceDetails.country with the corresponding country
              setValue('audienceDetails.country', selectedLocale.country);
            }
          };

          return (
            <>
              <StyledDropdownSelect
                handleSelect={handleSelect}
                items={filteredLocales}
                trackingId={CAMPAIGNS_TRACKING_ID.CREATE_CAMPAIGN_LOCALE_INPUT}
                value={props.value}
              />
            </>
          );
        }}
      </FormFieldController>
    </div>
  );
};

const StyledDropdownSelect = styled(DropdownSelect)`
  margin-top: ${({ theme }) => theme.spacings[1]};
`;
